<template>
    <div v-if="profileModule">
        <Paper class="profile-module-root" :class="{ 'spotlight-warning': !isComplete }">
            <div v-if="loading && !mini">
                <ProfileModuleSkeleton />
            </div>
            <div v-else-if="isAuthenticated" class="content p-2">
                <div
                    v-if="!dense"
                    id="profile-info"
                    v-b-tooltip.hover.bottom
                    class="py-2 px-3 clickable"
                    :title="
                        isComplete
                            ? $t('components.smart.profileModule.toProfileHint')
                            : $t('components.smart.profileModule.toProfileUncompletedHint')
                    "
                    @click="toSpotlight"
                >
                    <Avatar :image="profileImage">{{ firstLetterInName }}</Avatar>

                    <h6
                        style="grid-area: name; align-self: end; font-size: 18px; line-height: 26px"
                        class="nowrap"
                    >
                        {{ name ? name : email }}
                    </h6>
                    <p
                        v-if="name"
                        style="grid-area: email; align-self: start; font-size: 12px"
                        class="text-muted nowrap"
                    >
                        {{ email }}
                    </p>
                </div>
                <div v-if="!mini">
                    <div v-if="!dense" class="p-2">
                        <Divider />
                    </div>

                    <template v-if="score != 100">
                        <div id="spotlight-overview" class="p-2">
                            <h6 style="justify-self: start; width: min-content; min-width: 100px">
                                {{ $t('components.smart.profileModule.profileCompletion') }}
                            </h6>
                            <h6
                                style="justify-self: end; font-size: 40px"
                                class="font-weight-light"
                            >
                                {{ score }}%
                            </h6>
                            <b-progress
                                id="progress-secondary"
                                :value="score"
                                style="grid-column: span 2"
                                height="7px"
                            />
                            <p
                                style="grid-column: span 2; margin: 10px 0; font-size: 14px"
                                class="subtitle text-muted"
                            >
                                {{
                                    $t(
                                        'components.smart.profileModule.profileCompletionEncouragement',
                                        { percentageLeft: 100 - score }
                                    )
                                }}
                            </p>
                        </div>

                        <div v-if="!dense" class="mx-2">
                            <Divider />
                        </div>

                        <div v-if="!dense" class="mx-2 my-3">
                            <BaseButton class="w-100" variant="secondary" @click="toSpotlight">{{
                                $t('components.smart.profileModule.gotoProfilePage')
                            }}</BaseButton>
                        </div>
                    </template>
                    <template v-else>
                        <div class="p-2">
                            <h6>{{ $t('components.smart.profileModule.profileCompleted') }}</h6>
                            <p
                                v-if="isDiscoveredModeEnabled || hasAssessments"
                                style="margin: 10px 0; font-size: 14px"
                                class="subtitle text-muted"
                            >
                                {{
                                    isDiscoveredModeEnabled
                                        ? $t(
                                              'components.smart.profileModule.profileCompletedBeDiscoveredMode'
                                          )
                                        : $t(
                                              'components.smart.profileModule.profileCompletedNotBeDiscoveredMode'
                                          )
                                }}
                            </p>
                        </div>

                        <div>
                            <div class="mx-2">
                                <Divider />
                            </div>

                            <div class="mx-2 my-3">
                                <BaseButton
                                    v-if="!isDiscoveredModeEnabled"
                                    class="w-100"
                                    variant="secondary"
                                    :disabled="isActionLoading"
                                    @click="turnOnDiscoveredMode"
                                >
                                    <Spinner v-if="isActionLoading" class="mr-2" size="xs" />

                                    {{
                                        $t('components.smart.profileModule.turnOnBeDiscoveredMode')
                                    }}</BaseButton
                                >
                                <BaseButton
                                    v-else-if="hasAssessments"
                                    class="w-100"
                                    variant="secondary"
                                    @click="goToAssessments"
                                    >{{
                                        $t('components.smart.profileModule.gotoTestsPage')
                                    }}</BaseButton
                                >
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div v-else>
                <AuthIndication :dense="dense" @click="$emit('click')" />
            </div>
        </Paper>
        <TermsAndConds v-if="!mini" style="margin-top: var(--grid-gap)" />
    </div>
</template>

<script>
import Vue from 'vue';

// Component specific imports
import AuthIndication from './components/AuthIndication.vue';
import ProfileModuleSkeleton from '~/components/skeletons/ProfileModuleSkeleton.vue';
import TermsAndConds from '~/shared/TermsAndConds.vue';

// Utils import
import { PROFILE_MODULE, HAS_ASSESSMENTS } from '~/router';
import IdentityService from '~/services/IdentityService';
import CandidateService from '~/services/CandidateService';
import AuthService from '~/services/AuthService';
import {
    CandidateProfileScorer,
    CandidateProfileStatus,
} from '~/utils/candidate/CandidateProfileScorer';
import Spinner from '~/components/builders/Spinner.vue';
import DialogService from '~/services/DialogService';

//This needs a slight rewrite to enable showing terms/conds on schools without profile modules (only terms + pp, not school/employer)
export default Vue.extend({
    name: 'ProfileModule',
    components: { AuthIndication, ProfileModuleSkeleton, TermsAndConds, Spinner },
    props: {
        dense: Boolean,
        limit: {
            type: Number,
            default: 100,
        },
        mini: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        profileModule: PROFILE_MODULE,
        hasAssessments: HAS_ASSESSMENTS,

        identityService: new IdentityService(),
        candidateService: new CandidateService(),
        error: null,

        profileScore: null,

        isActionLoading: false,
    }),
    computed: {
        profile: {
            get() {
                return this.$store.getters['profileModule/getUser']();
            },
            set(value) {
                this.$store.commit('profileModule/setUser', value);
            },
        },
        candidate: {
            get() {
                return this.$store.getters['profileModule/getCandidate']();
            },
            set(value) {
                this.$store.commit('profileModule/setCandidate', value);
            },
        },
        workAuthorizations: {
            get() {
                return this.$store.getters['profileModule/getWorkAuthorizations']();
            },
            set(value) {
                this.$store.commit('profileModule/setWorkAuthorizations', value);
            },
        },
        score() {
            return this.profileScore ? this.profileScore.progress : 0;
        },
        name() {
            const data = this.data();
            return data.firstName && data.lastName
                ? data.firstName + ' ' + data.lastName
                : data.firstName || data.lastName || '';
        },
        email() {
            return this.data().email || '';
        },
        profileImage() {
            return this.data().avatar;
        },
        firstLetterInName() {
            return this.data().firstLetterInName;
        },
        isAuthenticated() {
            return this.$interfaces.logInModule.isLoggedIn();
        },
        isComplete() {
            return this.profileScore ? this.profileScore.progress === 100 : false;
        },
        isDiscoveredModeEnabled() {
            return this.candidate
                ? this.candidate.profileVisibility === 'public_to_recruiters'
                : false;
        },
        loading() {
            return !this.profile || !this.candidate || !this.workAuthorizations;
        },
    },
    methods: {
        data() {
            return this.profile || {};
        },
        toSpotlight() {
            this.$router.push(this.$URLS.spotlight);
        },
        goToAssessments() {
            this.$router.push(this.$URLS.assessments.concat(this.$URLS.assessmentCenter));
        },
        turnOnDiscoveredMode() {
            // Check if user has completed all required fields
            const scorer = new CandidateProfileScorer(
                this.candidate,
                this.profile,
                this.workAuthorizations
            );
            const score = scorer.getBeDiscoveredScore();
            if (score.status === CandidateProfileStatus.INCOMPLETE) {
                DialogService.openMyProfileBeDiscoveredDialog(scorer);
                return;
            }

            this.isActionLoading = true;
            this.candidateService
                .setProfileVisibility('public_to_recruiters')
                .then(() => {
                    this.refreshData();
                })
                .finally(() => {
                    this.isActionLoading = false;
                });
        },
        refreshData() {
            return AuthService.loadUserProfile().then(([user, candidate, wa]) => {
                this.profile = user;
                this.candidate = candidate;
                this.workAuthorizations = wa;
            });
        },
        calculateProfileScore() {
            if (this.candidate && this.profile) {
                this.profileScore = new CandidateProfileScorer(
                    this.candidate,
                    this.profile,
                    this.workAuthorizations
                ).getProfileScore();
            }
        },
    },
    watch: {
        profile: {
            immediate: true,
            handler() {
                this.calculateProfileScore();
            },
        },
        candidate: {
            immediate: true,
            handler() {
                this.calculateProfileScore();
            },
        },
        workAuthorizations: {
            immediate: true,
            handler() {
                this.calculateProfileScore();
            },
        },
    },
});
</script>

<style scoped>
.profile-module-root {
    position: relative;
}
.content {
    position: relative;
}
#profile-info {
    padding: 5px 0;
    display: grid;
    grid-template-columns: 40px auto;
    grid-column-gap: 5px;
    grid-template-rows: 20px 20px;
    grid-template-areas:
        'img name'
        'img email';
}
#profile-info > * {
    margin: 0;
    padding: 0;
}

#spotlight-overview {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 50px 20px auto;
}

.spotlight-action {
    display: flex;
    flex-direction: row;
    border: none;
    border-top: 1px solid #e5e5e5;
    color: #8e67c3;
}
.spotlight-action:hover {
    border-top: 1px solid #e5e5e5;
    background-color: rgba(100, 100, 100, 0.1);
}

#spotlight-actions {
    margin-bottom: 1px;
}

.spotlight-warning {
    color: var(--danger);
    border-width: 2px;
    border: solid;
}
</style>

<style>
#progress-secondary > *:first-child {
    background-color: var(--secondary) !important;
}
</style>
